import { Checkbox, FormGroup, Spacer, TextInput } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type {
    IOrthCV1DiagnosticReportCustomControlData,
    IOrthCV1DiagnosticReportCustomControlDataBevindingen,
} from '../../../../../../common/validation/customControls/ophthalmology';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces/';
import { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import { Feedback } from '../../../../elements/common';

interface IOrthCV1DiagnosticReportBevindingenProps extends ICustomControlDiagnosticReportAddEditProps {
    resetWerkdiagnose: () => void;
}

export function OrthCV1DiagnosticReportAddEditBevindingen(props: IOrthCV1DiagnosticReportBevindingenProps) {
    const customControlData: IOrthCV1DiagnosticReportCustomControlData = props.diagnosticReport.customControlData;
    const [isAndersTextBevindingenChecked, setIsAndersTextBevindingenChecked] = useState(false);
    const [hasErrorAndersText, setHasErrorAndersText] = useState(false);
    const [bevindingenError, setBevindingenError] = useState('');

    useEffect(() => {
        if (hasErrorAndersText) {
            setBevindingenError('any_required');
        } else {
            setBevindingenError(props.errors.get('customControlData.bevindingen') || '');
        }
    }, [props.errors, hasErrorAndersText]);

    useEffect(() => {
        setHasErrorAndersText(
            isAndersTextBevindingenChecked && !props.diagnosticReport.customControlData.bevindingen.andersText
        );
    }, [isAndersTextBevindingenChecked, props.diagnosticReport.customControlData.bevindingen.andersText]);

    return (
        <FormGroup
            hasErrors={props.errors.has('customControlData.bevindingen')}
            isRequired
            label="Bevindingen"
        >
            <Spacer
                lastChildHasNoSpacing
                spacing="small"
            >
                {bevindingenOption('niet_te_beoordelen', 'Niet te beoordelen')}
                {bevindingenOption('geen_afwijkingen', 'Geen afwijkingen')}
            </Spacer>
            {bevindingenOption('andersText', '')}
            <Feedback error={bevindingenError} />
        </FormGroup>
    );

    function bevindingenOption(name: keyof IOrthCV1DiagnosticReportCustomControlDataBevindingen, text: string) {
        const bevindingen: IOrthCV1DiagnosticReportCustomControlDataBevindingen =
            props.diagnosticReport.customControlData.bevindingen;
        const nameOd = (name + '_od') as keyof IOrthCV1DiagnosticReportCustomControlDataBevindingen;
        const nameOs = (name + '_os') as keyof IOrthCV1DiagnosticReportCustomControlDataBevindingen;

        return (
            <>
                {name === 'andersText' && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            checked={isAndersTextBevindingenChecked}
                            className="automation-bevindingen-anders"
                            onChange={async (event) => {
                                setIsAndersTextBevindingenChecked(!isAndersTextBevindingenChecked);
                                if (!event.target.checked) {
                                    await handleChangeBevindingen('andersText', null);
                                }
                            }}
                        />
                        <TextInput
                            className={`automation-bevindingen-andersText ${hasErrorAndersText ? 'has-danger' : ''}`}
                            onChange={async (event) => {
                                setIsAndersTextBevindingenChecked(event.target.value.length > 0);
                                handleChangeBevindingen('andersText', event.target.value ? event.target.value : null);
                            }}
                            placeholder="Anders, namelijk..."
                            style={{ marginLeft: '12px' }}
                            value={customControlData.bevindingen.andersText || ''}
                        />
                    </div>
                )}

                {name !== 'andersText' && (
                    <Checkbox
                        checked={props.diagnosticReport.customControlData.bevindingen[name]}
                        className={`automation-bevindingen-${name}`}
                        label={text}
                        onChange={async (event) => {
                            handleChangeBevindingen(name, event.target.checked);
                        }}
                    />
                )}
                {(bevindingen[name] ||
                    (name === 'andersText' && isAndersTextBevindingenChecked && !hasErrorAndersText)) && (
                    <Spacer
                        lastChildHasNoSpacing={true}
                        spacing="small"
                        style={{ paddingLeft: 32, paddingTop: 8 }}
                    >
                        <Checkbox
                            checked={!!bevindingen[nameOd]}
                            className={`automation-bevindingen-${name}-od`}
                            label="OD"
                            onChange={async (event) => {
                                handleChangeBevindingen(nameOd, event.target.checked);
                            }}
                        />
                        <Checkbox
                            checked={!!bevindingen[nameOs]}
                            className={`automation-bevindingen-${name}-os`}
                            label="OS"
                            onChange={async (event) => {
                                handleChangeBevindingen(nameOs, event.target.checked);
                            }}
                        />
                    </Spacer>
                )}
            </>
        );
    }

    async function handleChangeBevindingen<K extends keyof IOrthCV1DiagnosticReportCustomControlDataBevindingen>(
        name: K,
        value: IOrthCV1DiagnosticReportCustomControlDataBevindingen[K]
    ) {
        const names = ['niet_te_beoordelen', 'geen_afwijkingen', 'andersText'];
        const updatedBevindingen = {
            ...props.diagnosticReport.customControlData.bevindingen,
            [name]: value,
        };

        if (names.includes(name) && !value) {
            updatedBevindingen[`${name}_od`] = false;
            updatedBevindingen[`${name}_os`] = false;
        }

        await props.handleChangeCustomControl({ bevindingen: updatedBevindingen });
        props.validateOneCustomControl('bevindingen');

        if (updatedBevindingen.niet_te_beoordelen && !updatedBevindingen.geen_afwijkingen) {
            props.resetWerkdiagnose();
            props.handleChangeCustomControl({ conclusie_diagnose: ConclusieDiagnoseOption.nog_geen_diagnose });
        } else if (updatedBevindingen.geen_afwijkingen) {
            props.resetWerkdiagnose();
            props.handleChangeCustomControl({ conclusie_diagnose: ConclusieDiagnoseOption.geen_afwijkingen });
        }
    }
}
